import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  Link2,
  Users,
  Crosshair,
  Shield,
  Home,
  Crown,
  Video,
  Radio,
  Swords,
  Skull,
  Flag,
  Warehouse,
  MessageSquare,
  Megaphone,
  AlertTriangle,
  BadgeCheck,
  Gavel,
  Handshake,
  Menu as MenuIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import Markdown from "./Markdown";
import SubsectionDivider from "./SubsectionDivider";

// Icon registry — keep the keys in sync with backend `RULES_ICON_NAMES`.
// `Zombie` is an alias for lucide's Skull (closest match without adding deps).
export const RULES_ICON_MAP = {
  Users, Crosshair, Shield, Home, Crown, Video, Radio, Swords, Skull, Flag,
  Warehouse, MessageSquare, Megaphone, AlertTriangle, BadgeCheck, Gavel, Handshake,
  Zombie: Skull,
};

/**
 * Rules page — GitBook-style handbook.
 * Everything below the header is driven by the `SECTIONS` array. Edit or add
 * rules by changing the data; the sidebar nav, accordion, and scroll-spy all
 * rebuild themselves automatically.
 */

const DEFAULT_META = {
  last_updated: "February 13, 2026",
  server_owner: "OcciferCody",
};

const DEFAULT_WELCOME = {
  heading: "Welcome to What's Left of Us",
  body:
    "This is a PvE-focused, story-rich Project Zomboid roleplay server built for long-form survival together. Whether you're barricading a farmhouse, running the Louisville checkpoint, or scavenging Muldraugh at dusk — the world only works if we all commit to the fiction. By connecting to the server, submitting a whitelist application, or participating in our Discord community, you agree to abide by every rule in this handbook.",
};

const DEFAULT_FINAL = {
  heading: "Final Statement",
  body:
    "These rules exist to protect the story we're all trying to tell together. Staff decisions are final. If you feel a call was wrong, open a ticket in Discord — but don't drag it into public channels. Above all: play with respect, play with intent, and take care of each other outside the game.",
  tagline: "Stay quiet. Stay alive.",
};

const DEFAULT_PENDING_RULES = {
  enabled: true,
  heading: "Pending Rules + Migration",
  intro:
    "These are the eight original rules the server launched with. They remain in force while the sections above are being finalised. If anything above contradicts a rule here, the section above wins.",
  items: [
    { title: "Stay in character at all times", body: "OOC chat is permitted only in designated channels. Breaking immersion ruins the experience for everyone.", sev: "warn" },
    { title: "No metagaming or stream-sniping", body: "Information your character does not know cannot be used. Watch streams on a 30-minute delay or not at all.", sev: "kick" },
    { title: "No KOS without RP build-up", body: "Initiation, hostility, motive. You must give the other player a chance to engage before a kill.", sev: "kick" },
    { title: "No combat logging", body: "Logging out within 15 minutes of a hostile encounter will result in immediate review and possible roll-back.", sev: "ban" },
    { title: "No griefing or base destruction without RP cause", body: "Raids are allowed and encouraged — but they must be earned through RP.", sev: "ban" },
    { title: "Respect everyone OOC", body: "Hate speech, slurs, harassment, and bigotry are not tolerated.", sev: "ban" },
    { title: "One whitelist per person", body: "Alts must be approved by staff. Hidden alts will result in a ban of the main account.", sev: "ban" },
    { title: "Report, don't retaliate", body: "If a player breaks rules, open a ticket. Don't take justice into your own hands.", sev: "warn" },
  ],
};

const DEFAULT_SECTIONS = [
  { id: "general-roleplay", number: "01", title: "General Roleplay Rules", icon: "Users", subsections: [] },
  { id: "combat-pvp", number: "02", title: "Combat & PvP Rules", icon: "Crosshair", subsections: [] },
  { id: "base-raiding", number: "03", title: "Base & Raiding Rules", icon: "Home", subsections: [] },
  { id: "staff-admin", number: "04", title: "Staff & Admin Rules", icon: "Crown", subsections: [] },
  { id: "streamer-rules", number: "05", title: "Streamer Rules", icon: "Video", subsections: [] },
];

// Removed: hardcoded WIP_RULES — moved to backend `content.pending_rules` so
// super-admins can edit each item live from the Rules editor.

const SEV_STYLES = {
  warn: "bg-yellow-900/15 text-yellow-400/90 border-yellow-700/40",
  kick: "bg-orange-900/15 text-orange-400/90 border-orange-700/40",
  ban: "bg-red-900/20 text-red-400/90 border-red-700/40",
};

// Accent colour palette used by section highlights. Backend validates against
// the same set (RULES_SECTION_COLORS).
export const RULES_ACCENT_COLORS = [
  "#C0A875", "#C0392B", "#4ADE80", "#FFB347",
  "#7EC4CF", "#B58FE0", "#E27B7B", "#8D9A87",
];
const DEFAULT_ACCENT = "#C0A875";

// Convert a rule item to its canonical {text, sev} shape. Legacy strings pass
// through as objects with null severity.
function normaliseItem(x) {
  if (typeof x === "string") return { text: x, sev: null };
  if (x && typeof x === "object") return { text: x.text || "", sev: x.sev || null };
  return { text: String(x || ""), sev: null };
}

const HEADER_OFFSET = 80;

function copyDeepLink(anchor) {
  // window.location.origin already respects the deployed hostname, so this
  // produces both preview + live URLs correctly. Fallback for extremely old
  // browsers uses a hidden textarea + document.execCommand.
  const url = `${window.location.origin}/rules#${anchor}`;
  const done = () => toast.success("Link copied — paste it in Discord.");
  const fail = () => toast.error("Couldn't copy — long-press the URL bar instead.");
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(done, fail);
    return;
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    done();
  } catch {
    fail();
  }
}

function CopyLinkButton({ anchor, accent = "#8D9A87", label }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        copyDeepLink(anchor);
      }}
      title={label || "Copy deep-link to this section"}
      aria-label={label || `Copy link to ${anchor}`}
      data-testid={`rules-copy-link-${anchor}`}
      className="shrink-0 w-8 h-8 border border-[#283524] hover:border-[#C0A875] flex items-center justify-center transition-colors"
      style={{ color: accent }}
    >
      <Link2 size={13} />
    </button>
  );
}

export default function Rules() {
  const [content, setContent] = useState({
    meta: DEFAULT_META,
    welcome: DEFAULT_WELCOME,
    sections: DEFAULT_SECTIONS,
    final_statement: DEFAULT_FINAL,
    pending_rules: DEFAULT_PENDING_RULES,
  });
  const [open, setOpen] = useState(() => new Set());
  const [activeId, setActiveId] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Fetch the admin-editable content on mount. Falls back to bundled defaults.
  useEffect(() => {
    let cancelled = false;
    api.get("/content/rules")
      .then((res) => {
        if (cancelled) return;
        const c = res.data?.content;
        if (c && Array.isArray(c.sections) && c.sections.length) {
          setContent({
            meta: { ...DEFAULT_META, ...(c.meta || {}) },
            welcome: { ...DEFAULT_WELCOME, ...(c.welcome || {}) },
            sections: c.sections,
            final_statement: { ...DEFAULT_FINAL, ...(c.final_statement || {}) },
            pending_rules: { ...DEFAULT_PENDING_RULES, ...(c.pending_rules || {}) },
          });
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const META = content.meta;
  const SECTIONS = content.sections;
  const WELCOME = content.welcome;
  const FINAL_STATEMENT = content.final_statement;
  const PENDING = content.pending_rules || DEFAULT_PENDING_RULES;
  const showPending = PENDING.enabled !== false && (PENDING.items || []).length > 0;

  // Auto-open the first section whenever content loads or changes. Uses
  // `content` (not `SECTIONS`) as the dep so it runs when the API fetch
  // swaps in the real sections, not on every render. Since `content` is
  // only replaced on load, closing a section by hand won't re-trigger this.
  useEffect(() => {
    const first = content?.sections?.[0]?.id;
    if (!first) return;
    setOpen(new Set([first]));
    setActiveId(first);
  }, [content]);

  const sectionRefs = useRef({});
  // Stable subsection anchor id (mirrors Guide.jsx).
  const slug = (v) =>
    String(v || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const subId = (sec, sub, i) => `${sec.id}--${slug(sub.title) || `sub-${i + 1}`}`;
  const parentSectionFor = (id) => {
    for (const s of SECTIONS) {
      if (s.id === id) return s;
      for (let i = 0; i < (s.subsections || []).length; i++) {
        const sub = s.subsections[i];
        if (sub.title && subId(s, sub, i) === id) return s;
      }
    }
    return null;
  };
  const allIds = useMemo(
    () => {
      const ids = [];
      for (const s of SECTIONS) {
        ids.push(s.id);
        (s.subsections || []).forEach((sub, i) => {
          if (sub.title) ids.push(subId(s, sub, i));
        });
      }
      if (showPending) ids.push("wip-legacy");
      ids.push("severity-key");
      return ids;
    },
    [SECTIONS, showPending],
  );

  // Set to a timestamp when the user click-jumps; scroll-spy ignores itself
  // for a brief window afterwards so the smooth-scroll can't drift the
  // highlight past the intended section.
  const jumpLockUntil = useRef(0);

  // Scroll-spy: highlight the section whose top has scrolled past the header.
  useEffect(() => {
    const onScroll = () => {
      if (Date.now() < jumpLockUntil.current) return;
      let current = allIds[0];
      // Tight threshold — a section becomes active only once its top has
      // actually reached the header. A previous 40%-of-viewport window let
      // the *next* section win on click-jump for shorter sections.
      const threshold = HEADER_OFFSET + 24;
      for (const id of allIds) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - threshold <= 0) {
          current = id;
        }
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [allIds]);

  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const jumpTo = (id) => {
    // If target is a subsection, open its parent section so the anchor
    // lands on real content. (For plain section ids this just opens it.)
    const parent = parentSectionFor(id) || { id };
    setOpen((prev) => {
      if (prev.has(parent.id)) return prev;
      const next = new Set(prev);
      next.add(parent.id);
      return next;
    });
    // Eagerly highlight the target in the sidebar — scroll-spy alone doesn't
    // trigger because when multiple sections are open the scroll position may
    // still leave the previous section's top above the header threshold.
    setActiveId(id);
    // Suppress scroll-spy for ~1s while Lenis smoothly scrolls — otherwise
    // for shorter sections the momentum can drift into the next section and
    // steal the highlight.
    jumpLockUntil.current = Date.now() + 1000;
    setMobileNavOpen(false);
    // Wait one frame so the section actually has height when we scroll.
    requestAnimationFrame(() => {
      const target = sectionRefs.current[id];
      if (!target) return;
      if (window.__lenis) {
        window.__lenis.scrollTo(target, { offset: -HEADER_OFFSET, duration: 0.8 });
      } else {
        const y = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
  };

  return (
    <section id="rules" className="bg-[#0D110C] py-24 sm:py-32 border-t border-[#283524]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="label-tag text-[#C0A875]">// OFFICIAL SERVER HANDBOOK</span>
          <h1 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Server Rules
          </h1>
          <p className="mt-4 font-mono-ui text-xs sm:text-sm uppercase tracking-[0.25em] text-[#8D9A87]">
            Last Updated:{" "}
            <span className="text-[#C0A875]">{META.last_updated}</span>
          </p>
        </div>

        {/* Welcome box */}
        <div
          data-testid="rules-welcome"
          className="mt-10 bg-[#161D14] border border-[#283524] p-6 sm:p-8"
        >
          <h2 className="font-display uppercase text-[#E2E4DF] text-xl sm:text-2xl tracking-tight">
            {WELCOME.heading}
          </h2>
          <div className="mt-3 text-[#A8B5A0] text-sm sm:text-base leading-relaxed max-w-3xl">
            <Markdown>{WELCOME.body}</Markdown>
          </div>
        </div>

        {/* Two-column layout: sticky nav + accordion */}
        <div className="mt-10 lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10 items-start">
          {/* Mobile nav toggle */}
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setMobileNavOpen((v) => !v)}
              data-testid="rules-mobile-nav-toggle"
              className="w-full flex items-center justify-between gap-3 bg-[#161D14] border border-[#283524] px-4 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] text-[#E2E4DF]"
            >
              <span className="flex items-center gap-2">
                <MenuIcon size={14} className="text-[#C0A875]" />
                Jump to section
              </span>
              <ChevronDown
                size={14}
                className={`transition-transform ${mobileNavOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileNavOpen && (
              <NavList
                sections={SECTIONS}
                activeId={activeId}
                onClick={jumpTo}
                showPending={showPending}
                pendingHeading={PENDING.heading}
                className="mt-2 bg-[#161D14] border border-[#283524] divide-y divide-[#283524]"
              />
            )}
          </div>

          {/* Desktop sticky sidebar */}
          <aside
            data-testid="rules-side-nav"
            className="hidden lg:block sticky top-24 self-start"
          >
            <div className="label-tag text-[#5C6B51] mb-3 px-4">// Contents</div>
            <NavList
              sections={SECTIONS}
              activeId={activeId}
              onClick={jumpTo}
              showPending={showPending}
              pendingHeading={PENDING.heading}
              className="border-l border-[#283524]"
            />
          </aside>

          {/* Accordion */}
          <div className="space-y-4" data-testid="rules-accordion">
            {SECTIONS.map((sec) => {
              const isOpen = open.has(sec.id);
              const Icon = RULES_ICON_MAP[sec.icon] || Users;
              const accent = sec.color || DEFAULT_ACCENT;
              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  ref={(el) => (sectionRefs.current[sec.id] = el)}
                  data-testid={`rules-section-${sec.id}`}
                  className="bg-[#161D14] border scroll-mt-24 transition-colors"
                  style={{ borderColor: isOpen ? accent : "#283524" }}
                >
                  <div className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 sm:py-5 hover:bg-[#1B221A] transition-colors">
                    <button
                      type="button"
                      onClick={() => toggle(sec.id)}
                      aria-expanded={isOpen}
                      className="flex items-center gap-4 text-left flex-1 min-w-0"
                      data-testid={`rules-toggle-${sec.id}`}
                    >
                      <div
                        className="w-10 h-10 border flex items-center justify-center shrink-0 transition-colors"
                        style={{
                          borderColor: isOpen ? accent : "#283524",
                          color: isOpen ? accent : "#8D9A87",
                        }}
                      >
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block label-tag text-[#5C6B51]">SECTION {sec.number}</span>
                        <h3 className="mt-1 font-display uppercase text-[#E2E4DF] text-lg sm:text-xl tracking-tight">
                          {sec.title}
                        </h3>
                      </div>
                      <ChevronDown
                        size={20}
                        className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        style={{ color: isOpen ? accent : "#8D9A87" }}
                      />
                    </button>
                    <CopyLinkButton anchor={sec.id} accent={isOpen ? accent : "#8D9A87"} label={`Copy link to ${sec.title}`} />
                  </div>

                  {isOpen && (
                    <div
                      className="px-5 sm:px-6 pb-6 pt-2 space-y-6 border-t"
                      style={{ borderColor: "#283524" }}
                    >
                      {sec.subsections.map((sub, subIdx) => {
                        const subColor = sub.title_color || accent;
                        const sizeClass = {
                          compact: "text-sm",
                          huge: "text-2xl sm:text-3xl",
                        }[sub.title_size] || "text-lg sm:text-xl"; // normal default (bumped from `text-sm`)
                        const dividerStyle = sub.divider || "line";
                        const dividerColor =
                          sub.divider_color ||
                          (dividerStyle === "accent" || dividerStyle === "glyph"
                            ? subColor
                            : "#283524");
                        return (
                          <div key={sub.title}>
                            {subIdx > 0 && (
                              <SubsectionDivider style={dividerStyle} color={dividerColor} />
                            )}
                            <h4
                              id={sub.title ? subId(sec, sub, subIdx) : undefined}
                              ref={(el) => {
                                if (sub.title) sectionRefs.current[subId(sec, sub, subIdx)] = el;
                              }}
                              className={`font-display uppercase font-bold tracking-[0.12em] pb-1 scroll-mt-24 ${sizeClass}`}
                              style={{
                                color: subColor,
                                textDecoration: sub.title_underline ? "underline" : undefined,
                                textDecorationColor: sub.title_underline ? `${subColor}66` : undefined,
                                textUnderlineOffset: sub.title_underline ? "6px" : undefined,
                                textDecorationThickness: sub.title_underline ? "2px" : undefined,
                              }}
                            >
                              {sub.title}
                            </h4>
                            <ul className="mt-3 space-y-2">
                              {sub.items.map((raw, i) => {
                                const item = normaliseItem(raw);
                                return (
                                  <li
                                    key={i}
                                    className="flex items-start gap-3 text-[#A8B5A0] text-sm leading-relaxed"
                                  >
                                    <span className="shrink-0 select-none pt-0.5" style={{ color: subColor }}>›</span>
                                    <span className="flex-1"><Markdown inline>{item.text}</Markdown></span>
                                    {item.sev && (
                                      <span
                                        className={`shrink-0 inline-block px-2 py-0.5 border font-mono-ui text-[9px] tracking-[0.2em] uppercase ${SEV_STYLES[item.sev]}`}
                                      >
                                        {item.sev}
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pending Rules + Migration — data-driven, super-admin editable */}
            {showPending && (
              <div
                id="wip-legacy"
                ref={(el) => (sectionRefs.current["wip-legacy"] = el)}
                data-testid="rules-section-wip"
                className="bg-[#161D14] border border-yellow-700/40 scroll-mt-24 mt-8"
              >
                <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-yellow-700/40 flex flex-wrap items-center gap-3">
                  <span className="inline-block px-3 py-1 border font-mono-ui text-[10px] tracking-[0.25em] uppercase bg-yellow-900/15 text-yellow-400/90 border-yellow-700/40">
                    WIP
                  </span>
                  <h3 className="font-display uppercase text-[#E2E4DF] text-lg sm:text-xl tracking-tight flex-1 min-w-0">
                    {PENDING.heading || "Pending Rules + Migration"}
                  </h3>
                  <CopyLinkButton anchor="wip-legacy" accent="#EAB308" label="Copy link to Pending Rules" />
                </div>
                <div className="p-5 sm:p-6 space-y-3">
                  {PENDING.intro && (
                    <div className="text-[#A8B5A0] text-sm leading-relaxed max-w-3xl">
                      <Markdown>{PENDING.intro}</Markdown>
                    </div>
                  )}
                  <div className="mt-4 border border-[#283524] divide-y divide-[#283524]">
                    {(PENDING.items || []).map((r, idx) => {
                      const num = String(idx + 1).padStart(2, "0");
                      return (
                        <div
                          key={idx}
                          data-testid={`rule-${num}`}
                          className="p-4 sm:p-5 flex flex-col md:flex-row gap-3 md:gap-6 md:items-center"
                        >
                          <span className="label-tag text-[#C0A875] shrink-0 md:w-14">{num}</span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-display uppercase text-[#E2E4DF] text-base tracking-tight">
                              {r.title}
                            </h4>
                            {r.body && (
                              <div className="mt-1.5 text-[#8D9A87] text-sm leading-relaxed max-w-3xl">
                                <Markdown>{r.body}</Markdown>
                              </div>
                            )}
                          </div>
                          {r.sev && (
                            <span
                              className={`shrink-0 inline-block px-3 py-1.5 border font-mono-ui text-[10px] tracking-[0.25em] uppercase ${SEV_STYLES[r.sev] || SEV_STYLES.warn}`}
                            >
                              {r.sev}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Severity Key — standalone block for at-a-glance reference */}
            <div
              id="severity-key"
              ref={(el) => (sectionRefs.current["severity-key"] = el)}
              data-testid="rules-severity-key"
              className="bg-[#161D14] border border-[#283524] scroll-mt-24 mt-8"
            >
              <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-[#283524] flex items-center gap-3">
                <h3 className="font-display uppercase text-[#E2E4DF] text-lg sm:text-xl tracking-tight flex-1 min-w-0">
                  Severity Key
                </h3>
                <CopyLinkButton anchor="severity-key" accent="#C0A875" label="Copy link to Severity Key" />
              </div>
              <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className={`p-4 border ${SEV_STYLES.warn}`}>
                  <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase">Warn</div>
                  <div className="mt-1.5 text-[#A8B5A0] text-sm">Written notice.</div>
                </div>
                <div className={`p-4 border ${SEV_STYLES.kick}`}>
                  <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase">Kick</div>
                  <div className="mt-1.5 text-[#A8B5A0] text-sm">Temporary removal.</div>
                </div>
                <div className={`p-4 border ${SEV_STYLES.ban}`}>
                  <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase">Ban</div>
                  <div className="mt-1.5 text-[#A8B5A0] text-sm">Permanent.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final statement */}
        <div
          data-testid="rules-final-statement"
          className="mt-16 bg-[#161D14] border border-[#5C6B51] p-8 sm:p-12 text-center"
        >
          <h2 className="font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl tracking-tight">
            {FINAL_STATEMENT.heading}
          </h2>
          <div className="mt-4 text-[#A8B5A0] text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            <Markdown>{FINAL_STATEMENT.body}</Markdown>
          </div>
          {FINAL_STATEMENT.tagline && (
            <p className="mt-6 font-display uppercase text-[#C0A875] text-lg sm:text-xl tracking-[0.15em]">
              {FINAL_STATEMENT.tagline}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function NavList({ sections, activeId, onClick, showPending = true, pendingHeading = "Pending Rules", className = "" }) {
  const slug = (v) =>
    String(v || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const subId = (sec, sub, i) => `${sec.id}--${slug(sub.title) || `sub-${i + 1}`}`;

  return (
    <ul className={className} data-testid="rules-nav-list">
      {sections.map((s) => {
        const activeSection = activeId === s.id;
        const titledSubs = (s.subsections || [])
          .map((sub, i) => ({ sub, i }))
          .filter(({ sub }) => sub.title);
        const isCurrent =
          activeSection ||
          titledSubs.some(({ sub, i }) => subId(s, sub, i) === activeId);
        const Icon = RULES_ICON_MAP[s.icon] || Users;
        const accent = s.color || DEFAULT_ACCENT;
        return (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onClick(s.id)}
              data-testid={`rules-nav-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-l-2 -ml-px lg:-ml-[2px] ${
                activeSection
                  ? ""
                  : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#5C6B51]"
              }`}
              style={activeSection ? {
                borderColor: accent,
                color: accent,
                backgroundColor: `${accent}12`,
              } : undefined}
            >
              <Icon size={14} className="shrink-0" style={{ color: activeSection ? accent : undefined }} />
              <div className="min-w-0 flex-1">
                <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] opacity-70">
                  {s.number}
                </div>
                <div className="font-mono-ui text-[11px] uppercase tracking-[0.15em] truncate">
                  {s.title}
                </div>
              </div>
            </button>

            {/* Nested subsection bullets — mirrors the Guide sidebar. Only
                shown when the section is current AND has 2+ titled subs. */}
            {isCurrent && titledSubs.length >= 2 && (
              <ul
                className="ml-[38px] mt-0.5 mb-2 py-1 space-y-[1px]"
                data-testid={`rules-nav-subs-${s.id}`}
              >
                {titledSubs.map(({ sub, i }) => {
                  const sid = subId(s, sub, i);
                  const activeSub = activeId === sid;
                  const subAccent = sub.title_color || accent;
                  return (
                    <li key={sid}>
                      <button
                        type="button"
                        onClick={() => onClick(sid)}
                        data-testid={`rules-nav-sub-${sid}`}
                        className={`w-full flex items-center gap-2.5 text-left px-2 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.18em] truncate rounded-sm transition-colors ${
                          activeSub
                            ? "font-semibold"
                            : "text-[#5C6B51] hover:text-[#E2E4DF] hover:bg-[#161D14]"
                        }`}
                        style={activeSub ? { color: subAccent, background: `${subAccent}10` } : undefined}
                      >
                        <span
                          aria-hidden="true"
                          className="shrink-0 rounded-full transition-all"
                          style={{
                            width: activeSub ? 8 : 6,
                            height: activeSub ? 8 : 6,
                            background: activeSub ? subAccent : "transparent",
                            border: activeSub ? "0" : `1.5px solid #5C6B51`,
                          }}
                        />
                        <span className="truncate">{sub.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
      {showPending && (
        <li>
          <button
            type="button"
            onClick={() => onClick("wip-legacy")}
            data-testid="rules-nav-wip-legacy"
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-l-2 -ml-px lg:-ml-[2px] ${
              activeId === "wip-legacy"
                ? "border-yellow-500 text-yellow-400 bg-yellow-900/10"
                : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#5C6B51]"
            }`}
          >
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] px-1.5 py-0.5 border border-current shrink-0">
              WIP
            </span>
            <div className="font-mono-ui text-[11px] uppercase tracking-[0.15em] truncate">
              {pendingHeading || "Pending Rules"}
            </div>
          </button>
        </li>
      )}
      <li>
        <button
          type="button"
          onClick={() => onClick("severity-key")}
          data-testid="rules-nav-severity-key"
          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-l-2 -ml-px lg:-ml-[2px] ${
            activeId === "severity-key"
              ? "border-[#C0A875] text-[#C0A875] bg-[#C0A87512]"
              : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#5C6B51]"
          }`}
        >
          <BadgeCheck size={14} className="shrink-0" />
          <div className="font-mono-ui text-[11px] uppercase tracking-[0.15em] truncate">
            Severity Key
          </div>
        </button>
      </li>
    </ul>
  );
}
